import type {
  AgentSummary,
  CronJobView,
  RepoSummary,
  RunDetail,
  RunStatus,
  RunSummary,
  StatsDays,
  StatsResponse,
} from "@agentdash/shared";

// Shapes returned by /api/repos[/...] — mirror the api's RepoService output.
export interface RepoListEntry extends RepoSummary {
  cron_jobs: CronJobView[];
}
export interface ReposListResponse {
  repos: RepoListEntry[];
  ungrouped: { cron_jobs: CronJobView[] };
  agents: AgentSummary[];
}

export interface GhCommit {
  sha: string;
  short_sha: string;
  message: string;
  author: string | null;
  url: string;
  date: string;
}

export interface GhPullRequest {
  number: number;
  title: string;
  url: string;
  author: string;
  draft: boolean;
  updated_at: string;
}

export interface GhWorkflowRun {
  id: number;
  name: string;
  status: string;
  conclusion: string;
  url: string;
  head_sha: string;
  updated_at: string;
}

export interface RepoDetailResponse extends RepoSummary {
  cron_jobs: CronJobView[];
  agents: AgentSummary[];
  recent_runs: RunSummary[];
  github: {
    enabled: boolean;
    commits: GhCommit[];
    /** Human-readable hint when getRecentCommits failed (e.g. PAT missing
     * Contents:Read). Null on success or when the call wasn't made. */
    commits_error: string | null;
    open_prs: GhPullRequest[];
    latest_workflow_run: GhWorkflowRun | null;
  };
  dokploy: {
    enabled: boolean;
    compose: { composeId: string; name: string; status: string } | null;
  };
}

/** `status` may be several: the API matches any of them (`?status=ok,error`). */
export interface RunsQueryOpts {
  status?: RunStatus | readonly RunStatus[];
  limit?: number;
  offset?: number;
}

// Empty string = same-origin (production: API and SPA share the host).
// In dev, override with VITE_API_BASE_URL=http://localhost:4000 (set in .env.example).
const BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/**
 * Pull the human-readable message out of the API's `{error:{code,message}}`
 * error envelope (the convention every route follows). Returns null when the
 * body isn't in that shape, so callers can fall back to the HTTP status line
 * instead of dumping raw JSON at the user.
 */
function apiErrorMessage(body: unknown): string | null {
  if (body && typeof body === "object" && "error" in body) {
    const err = (body as { error: unknown }).error;
    if (
      err &&
      typeof err === "object" &&
      "message" in err &&
      typeof (err as { message: unknown }).message === "string"
    ) {
      return (err as { message: string }).message;
    }
  }
  return null;
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when we actually send one. A no-body POST
  // (pause/resume/run-now) with content-type: application/json trips Fastify's
  // FST_ERR_CTP_EMPTY_JSON_BODY parser error before the route runs.
  const hasBody = init?.body != null;
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: "include", // send session cookie on same-origin + cross-origin dev proxy
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    // Prefer the API's structured human message; fall back to the status line.
    const message =
      apiErrorMessage(body) ?? `HTTP ${res.status} ${res.statusText}`;
    throw new HttpError(res.status, message, body);
  }
  return (await res.json()) as T;
}

export interface AuthUser {
  email: string;
}

export const api = {
  // --- auth ---
  // `authEnabled: false` is the open-mode answer (no AUTH_* env on the API).
  authMe(): Promise<{ user: AuthUser } | { user: null; authEnabled: false }> {
    return http("/api/auth/me");
  },
  authLogin(email: string, password: string): Promise<{ user: AuthUser }> {
    return http("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  authLogout(): Promise<{ ok: true }> {
    return http("/api/auth/logout", { method: "POST" });
  },

  // --- runs ---
  listRuns(opts?: RunsQueryOpts): Promise<RunSummary[]> {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", [opts.status].flat().join(","));
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.offset) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return http(`/api/runs${qs ? `?${qs}` : ""}`);
  },
  getRun(traceId: string): Promise<RunDetail> {
    return http(`/api/runs/${encodeURIComponent(traceId)}`);
  },
  summarizeRun(traceId: string): Promise<{ status: "queued" }> {
    return http(`/api/runs/${encodeURIComponent(traceId)}/summarize`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  // --- repos ---
  listRepos(): Promise<ReposListResponse> {
    return http("/api/repos");
  },
  getRepo(slug: string): Promise<RepoDetailResponse> {
    // slug is `owner/name` — encode each segment so URLs containing dots etc. resolve.
    const [owner, name] = slug.split("/", 2);
    return http(`/api/repos/${encodeURIComponent(owner ?? "")}/${encodeURIComponent(name ?? "")}`);
  },

  listSpecFiles(owner: string, name: string): Promise<{ files: string[] }> {
    return http(
      `/api/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/spec`,
    );
  },
  getSpecFile(
    owner: string,
    name: string,
    filename: string,
  ): Promise<{ filename: string; content: string }> {
    return http(
      `/api/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/spec/${encodeURIComponent(filename)}`,
    );
  },

  // --- admin: settings ---
  getGithubTokenSetting(): Promise<{ configured: boolean; masked: string | null }> {
    return http("/api/admin/settings/github-token");
  },
  setGithubTokenSetting(token: string): Promise<{ configured: true; masked: string }> {
    return http("/api/admin/settings/github-token", {
      method: "PUT",
      body: JSON.stringify({ token }),
    });
  },
  deleteGithubTokenSetting(): Promise<{ configured: false; masked: null }> {
    return http("/api/admin/settings/github-token", { method: "DELETE" });
  },

  getDokploySetting(): Promise<{
    configured: boolean;
    api_url: string | null;
    masked_api_key: string | null;
  }> {
    return http("/api/admin/settings/dokploy");
  },
  setDokploySetting(
    api_url: string,
    api_key: string,
  ): Promise<{ configured: true; api_url: string; masked_api_key: string }> {
    return http("/api/admin/settings/dokploy", {
      method: "PUT",
      body: JSON.stringify({ api_url, api_key }),
    });
  },
  deleteDokploySetting(): Promise<{
    configured: false;
    api_url: null;
    masked_api_key: null;
  }> {
    return http("/api/admin/settings/dokploy", { method: "DELETE" });
  },

  getAnthropicSetting(): Promise<{
    configured: boolean;
    masked_api_key: string | null;
    model: string | null;
    enabled: boolean | null;
  }> {
    return http("/api/admin/settings/anthropic");
  },
  setAnthropicSetting(
    api_key: string,
    model?: string,
    enabled?: boolean,
  ): Promise<{
    configured: true;
    masked_api_key: string;
    model: string | null;
    enabled: boolean;
  }> {
    return http("/api/admin/settings/anthropic", {
      method: "PUT",
      body: JSON.stringify({ api_key, model, enabled }),
    });
  },
  deleteAnthropicSetting(): Promise<{
    configured: false;
    masked_api_key: null;
    model: null;
    enabled: null;
  }> {
    return http("/api/admin/settings/anthropic", { method: "DELETE" });
  },

  // --- admin: agents ---
  listAgents(): Promise<{
    agents: Array<{
      agent_id: string;
      hostname: string;
      last_seen_at: string | null;
      created_at: string;
    }>;
  }> {
    return http("/api/admin/agents");
  },
  registerAgent(
    agent_id: string,
    hostname: string,
  ): Promise<{ agent_id: string; hostname: string; token: string }> {
    return http("/api/admin/agents", {
      method: "POST",
      body: JSON.stringify({ agent_id, hostname }),
    });
  },
  deleteAgent(agent_id: string): Promise<{ ok: true }> {
    return http(`/api/admin/agents/${encodeURIComponent(agent_id)}`, {
      method: "DELETE",
    });
  },

  // --- stats ---
  getStats(days: StatsDays): Promise<StatsResponse> {
    return http(`/api/stats?days=${days}`);
  },

  // --- cron-job control plane ---
  // `cronJobId` is a sha256 hex digest today (tools/host-agent/src/crontab.ts),
  // so encoding is a no-op against every id the host-agent can emit — but that
  // invariant lives in another workspace, so encode here like every other
  // path-param call in this module rather than relying on it.
  pauseCronJob(cronJobId: string) {
    return http(`/api/cron-jobs/${encodeURIComponent(cronJobId)}/pause`, { method: "POST" });
  },
  resumeCronJob(cronJobId: string) {
    return http(`/api/cron-jobs/${encodeURIComponent(cronJobId)}/resume`, { method: "POST" });
  },
  runCronJobNow(cronJobId: string) {
    return http(`/api/cron-jobs/${encodeURIComponent(cronJobId)}/run-now`, { method: "POST" });
  },
  setCronJobSchedule(cronJobId: string, schedule: string) {
    return http(`/api/cron-jobs/${encodeURIComponent(cronJobId)}/schedule`, {
      method: "POST",
      body: JSON.stringify({ schedule }),
    });
  },
  setCronJobWrapped(cronJobId: string, wrapped: boolean) {
    return http(`/api/cron-jobs/${encodeURIComponent(cronJobId)}/wrapped`, {
      method: "POST",
      body: JSON.stringify({ wrapped }),
    });
  },
};

export const apiBaseUrl = BASE_URL;
